import React, { useState, useEffect } from 'react';
import {
  X,
  Heart,
  ExternalLink,
  Check,
  Play,
  Printer,
} from "lucide-react";
import { supabase } from '../supabase';

function getYouTubeId(url) {
  if (!url) return "";
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/
  );
  return match ? match[1] : "";
}

function PatternDetailModal({
  selectedPattern,
  initialTab,
  savedPatterns,
  stitches,
  stitchesLoading,
  isAdmin,
  colors,
  fonts,
  onClose,
  onSave,
  onTrackProject,
  onCustomize,
  onNavigateToStitch,
  showStartProjectModal,
  startingProjectPattern,
  startProjectNotes,
  setStartProjectNotes,
  onCancelStart,
  onConfirmStart,
}) {
  const [paletteColors, setPaletteColors] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab || 'details');
  useEffect(() => { setActiveTab(initialTab || 'details'); }, [initialTab]);
  const hasPatternContent = selectedPattern?.patternContent?.sections?.some(s => s.steps?.some(st => st.trim()));

  useEffect(() => {
    const yarnColors = selectedPattern?.yarnColors;
    if (!yarnColors || yarnColors.length === 0) {
      setPaletteColors([]);
      return;
    }
    const ids = yarnColors.map(y => y.id).filter(Boolean);
    if (ids.length === 0) { setPaletteColors([]); return; }
    supabase
      .from('yarn_library')
      .select('*')
      .in('id', ids)
      .then(({ data, error }) => {
        if (error) console.error('yarn_library fetch error:', error);
        // Merge library data with skeins/role from yarnColors, preserve order
        const merged = yarnColors
          .map(yc => {
            const lib = (data || []).find(d => d.id === yc.id);
            return lib ? { ...lib, skeins: yc.skeins, total_yards: yc.total_yards || null, role: yc.role } : null;
          })
          .filter(Boolean);
        setPaletteColors(merged);
      });
  }, [selectedPattern?.yarnColors]);

  return (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "2rem",
            overflow: "auto",
          }}
          onClick={onClose}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              maxWidth: "900px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              fontFamily: fonts.body,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: "1rem",
                left: "1rem",
                background: "rgba(255,255,255,0.9)",
                border: "none",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "1.5rem",
                color: colors.brownGray,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 10,
              }}
            >
              <X size={24} />
            </button>

           {/* Bookmark Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSave(selectedPattern);
            }}
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              background: savedPatterns.some(sp => sp.pattern_id === selectedPattern.id && sp.status === 'saved')
                ? colors.dustyRust
                : "rgba(255,255,255,0.95)",
              border: `2px solid ${colors.dustyRust}`,
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: savedPatterns.some(sp => sp.pattern_id === selectedPattern.id && sp.status === 'saved')
                ? "#fff"
                : colors.dustyRust,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 10,
              transition: "all 0.3s",
            }}
            title={savedPatterns.some(sp => sp.pattern_id === selectedPattern.id && sp.status === 'saved') ? "Saved!" : "Save pattern"}
          >
            {(() => {
              const isSaved = savedPatterns.some(sp => sp.pattern_id === selectedPattern.id && sp.status === 'saved');
              return <Heart size={16} fill={isSaved ? "currentColor" : "none"} />;
            })()}
          </button>

            {/* Hero Image */}
            <div
              style={{
                position: "relative",
                height: "300px",
                background: `linear-gradient(135deg, ${colors.sageGreen}, #8e9775)`,
                borderRadius: "20px 20px 0 0",
                overflow: "hidden",
              }}
            >
              {selectedPattern.image && (
                <img
                  src={selectedPattern.image}
                  alt={selectedPattern.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
              <div
                style={{
                  position: "absolute",
                  bottom: "1.5rem",
                  left: "2rem",
                  background: "rgba(255,255,255,0.95)",
                  padding: "0.5rem 1.25rem",
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "700",
                  color:
                    selectedPattern.difficulty === "Beginner Friendly"
                      ? "#9ec5d4"
                      : selectedPattern.difficulty === "Beginner"
                      ? colors.sageGreen
                      : selectedPattern.difficulty === "Intermediate"
                      ? colors.mustard
                      : colors.dustyRust,
                }}
              >
                {selectedPattern.difficulty}
              </div>
            </div>

            {/* Tabs — only show if pattern has content */}
            {hasPatternContent && (
              <div style={{ display: "flex", borderBottom: `2px solid ${colors.borderGreige}`, padding: "0 2.5rem" }}>
                {[
                  { id: 'details', label: 'Details' },
                  { id: 'pattern', label: 'Pattern' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                    padding: "1rem 1.5rem",
                    background: "transparent",
                    border: "none",
                    borderBottom: activeTab === tab.id ? `3px solid ${colors.dustyRust}` : "3px solid transparent",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    color: activeTab === tab.id ? colors.charcoal : colors.brownGray,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}>{tab.label}</button>
                ))}
              </div>
            )}

            {/* Pattern Content Tab */}
            {hasPatternContent && activeTab === 'pattern' && (
              <div style={{ padding: "2.5rem" }}>
                {/* Pattern Photos */}
                {selectedPattern.patternPhotos?.filter(u => u).length > 0 && (
                  <div style={{ display: "flex", gap: "1rem", overflowX: "auto", marginBottom: "2rem" }}>
                    {selectedPattern.patternPhotos.filter(u => u).map((url, i) => (
                      <img key={i} src={url} alt={`Pattern photo ${i + 1}`} style={{ height: "200px", borderRadius: "12px", objectFit: "cover", flexShrink: 0 }} />
                    ))}
                  </div>
                )}

                {/* Print button */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1.5rem" }}>
                  <button onClick={() => {
                    const win = window.open('', '_blank');
                    const sections = selectedPattern.patternContent.sections;
                    const photos = (selectedPattern.patternPhotos || []).filter(u => u);
                    win.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>${selectedPattern.title} — The Single Loop</title>
                        <link href="https://fonts.googleapis.com/css2?family=Karla:wght@400;600;700&family=Pacifico&display=swap" rel="stylesheet">
                        <style>
                          body { font-family: 'Karla', sans-serif; max-width: 700px; margin: 2rem auto; padding: 0 2rem; color: #2a2a2a; }
                          h1 { font-family: 'Pacifico', cursive; font-size: 2rem; color: #c1795b; margin-bottom: 0.25rem; }
                          .credit { font-size: 0.85rem; color: #8b7355; margin-bottom: 2rem; }
                          h2 { font-size: 1.2rem; border-bottom: 2px solid #e8e0d5; padding-bottom: 0.4rem; margin-top: 2rem; color: #2a2a2a; }
                          ol { padding-left: 1.5rem; }
                          li { margin-bottom: 0.75rem; line-height: 1.6; font-size: 1rem; }
                          .photos { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 2rem; }
                          .photos img { max-height: 200px; border-radius: 8px; object-fit: cover; }
                          .footer { margin-top: 3rem; padding-top: 1rem; border-top: 2px solid #e8e0d5; font-size: 0.8rem; color: #8b7355; }
                          @media print { body { margin: 1rem; } }
                        </style>
                      </head>
                      <body>
                        <h1>${selectedPattern.title}</h1>
                        <div class="credit">From The Single Loop — thesingleloop.com</div>
                        ${photos.length ? `<div class="photos">${photos.map(u => `<img src="${u}" />`).join('')}</div>` : ''}
                        ${sections.map(s => `
                          ${s.title ? `<h2>${s.title}</h2>` : ''}
                          <ol>${s.steps.filter(st => st.trim()).map(st => `<li>${st}</li>`).join('')}</ol>
                        `).join('')}
                        <div class="footer">Pattern via The Single Loop · thesingleloop.com</div>
                      </body>
                      </html>
                    `);
                    win.document.close();
                    win.focus();
                    setTimeout(() => win.print(), 500);
                  }} style={{
                    display: "flex", alignItems: "center", gap: "0.4rem",
                    padding: "0.6rem 1.2rem", background: colors.bgCream, border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "50px", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer", color: colors.brownGray,
                  }}>
                    <Printer size={14} /> Print / Save PDF
                  </button>
                </div>

                {/* Sections */}
                {selectedPattern.patternContent.sections.map((section, sIdx) => (
                  <div key={sIdx} style={{ marginBottom: "2rem" }}>
                    {section.title && (
                      <h3 style={{ fontFamily: fonts.display, fontSize: "1.3rem", color: colors.charcoal, marginBottom: "1rem", borderBottom: `2px solid ${colors.borderGreige}`, paddingBottom: "0.5rem" }}>
                        {section.title}
                      </h3>
                    )}
                    <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                      {section.steps.filter(s => s.trim()).map((step, stIdx) => (
                        <li key={stIdx} style={{ marginBottom: "0.75rem", fontSize: "1rem", color: colors.charcoal, lineHeight: 1.6 }}>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            )}

            {/* Details Tab (existing content) */}
            {(!hasPatternContent || activeTab === 'details') && (
            <div style={{ padding: "2.5rem" }}>
              {/* Title & Attribution */}
              <div style={{ marginBottom: "2rem" }}>
                <h2
                  style={{
                    fontSize: "2rem",
                    fontFamily: fonts.display,
                    color: colors.charcoal,
                    marginBottom: "0.75rem",
                    lineHeight: 1.2,
                  }}
                >
                  {selectedPattern.title}
                </h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    fontSize: "0.9rem",
                    color: colors.brownGray,
                    marginBottom: "1rem",
                  }}
                >
                  <span>
                    From: <strong>{selectedPattern.siteName}</strong>
                  </span>
                  {selectedPattern.sourceUrl && (
                    <a
                      href={selectedPattern.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", fontSize: "0.8rem", fontWeight: "600", color: colors.brownGray, textDecoration: "none" }}
                    >
                      View Pattern <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>

         {/* Engagement Stats */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem 1.5rem",
              padding: "1rem",
              background: colors.bgCream,
              borderRadius: "12px",
              marginTop: "1rem",
              fontSize: "0.85rem",
              color: colors.darkGray,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <Heart size={16} color={colors.dustyRust} />
              <span style={{ fontWeight: "600" }}>
                {selectedPattern.save_count || 0}
              </span>{" "}
              saves
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <Check size={16} color={colors.sageGreen} />
              <span style={{ fontWeight: "600" }}>
                {selectedPattern.complete_count || 0}
              </span>{" "}
              completed
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              👁️
              <span style={{ fontWeight: "600" }}>
                {(selectedPattern.view_count || 0).toLocaleString()}
              </span>{" "}
              views
            </div>
          </div>

              {/* Description */}
              {selectedPattern.description && (
                <p
                  style={{
                    fontSize: "1rem",
                    color: colors.darkGray,
                    lineHeight: 1.6,
                    marginBottom: "2rem",
                  }}
                >
                  {selectedPattern.description}
                </p>
              )}

              {/* The Single Loop Thoughts */}
              {selectedPattern.curatorNotes && (
                <div
                  style={{
                    background: colors.bgCream,
                    padding: "1.25rem",
                    borderRadius: "12px",
                    borderLeft: `4px solid ${colors.mustard}`,
                    marginBottom: "2rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Tip
                  </div>
                  <p
                    style={{
                      fontSize: "0.95rem",
                      color: colors.darkGray,
                      margin: 0,
                      fontStyle: "italic",
                    }}
                  >
                    "{selectedPattern.curatorNotes}"
                  </p>
                </div>
              )}

              {/* Technical Specs Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: "1.5rem",
                  marginBottom: "2rem",
                  padding: "1.5rem",
                  background: colors.bgCream,
                  borderRadius: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Difficulty
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: colors.charcoal,
                    }}
                  >
                    {selectedPattern.difficulty}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Category
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: colors.charcoal,
                    }}
                  >
                    {Array.isArray(selectedPattern.category)
                      ? selectedPattern.category.join(", ")
                      : selectedPattern.category}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Yarn Weight
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: colors.charcoal,
                    }}
                  >
                    {selectedPattern.yarnWeight}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Yarn Material
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: colors.charcoal,
                    }}
                  >
                    {selectedPattern.yarnMaterial}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Hook Size
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: colors.charcoal,
                    }}
                  >
                    {Array.isArray(selectedPattern.hook)
                      ? selectedPattern.hook.join(", ")
                      : selectedPattern.hook}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Gauge
                  </div>
                  <div style={{ fontSize: "1.1rem", fontWeight: "700", color: colors.charcoal }}>
                    {(() => {
                      const entries = selectedPattern.gaugeEntries;
                      if (entries && entries.length > 0) {
                        return entries.map((entry, idx) => {
                          let text = '';
                          const unitLabel = entry.unit === '2in' ? '2"' : '4"';
                          if (entry.type === 'standard') text = `${entry.stitches} st${entry.rows ? ` × ${entry.rows} rows` : ''} = ${unitLabel}`;
                          else if (entry.type === 'granny') text = `Finished square = ${entry.square_inches}"`;
                          else if (entry.type === 'c2c') text = `${entry.blocks} blocks = ${unitLabel}`;
                          else if (entry.type === 'note') text = entry.note || '';
                          const label = entry.label ? ` (${entry.label})` : '';
                          return <div key={idx} style={idx > 0 ? { marginTop: "0.25rem" } : {}}>{text}{label}</div>;
                        });
                      }
                      return '—';
                    })()}
                  </div>
                </div>

                {selectedPattern.constructionMethod && (
                  <div>
                    <div style={{ fontSize: "0.8rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.5rem" }}>
                      Construction
                    </div>
                    <div style={{ fontSize: "1.1rem", fontWeight: "700", color: colors.charcoal }}>
                      {selectedPattern.constructionMethod}
                    </div>
                  </div>
                )}

                <div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total Yarn
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: colors.charcoal,
                    }}
                  >
                    {selectedPattern.totalYards} yards
                  </div>
                </div>

                {selectedPattern.originalSize &&
                  (selectedPattern.originalSize.width ||
                    selectedPattern.originalSize.height) && (
                    <div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "700",
                          color: colors.brownGray,
                          textTransform: "uppercase",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Original Size
                      </div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "700",
                          color: colors.charcoal,
                        }}
                      >
                        {selectedPattern.originalSize.width}" ×{" "}
                        {selectedPattern.originalSize.height}"
                      </div>
                    </div>
                  )}
              </div>

              {/* Stitches Used */}
              {selectedPattern.stitches && selectedPattern.stitches.length > 0 && !stitchesLoading && (
                  <div
                    style={{
                      background: "#fff",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "12px",
                      padding: "1.5rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: colors.charcoal,
                        marginBottom: "1rem",
                      }}
                    >
                      Stitches Used
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.75rem",
                      }}
                    >
                      {selectedPattern.stitches.map((stitchId) => {
                        const stitch = stitches?.find(
                          (s) => s.id === stitchId
                        );
                        return stitch ? (
                          <button
                            key={stitchId}
                            onClick={() => {
                              onNavigateToStitch(stitchId);
                            }}
                            style={{
                              padding: "0.75rem 1rem",
                              background: colors.bgCream,
                              border: `2px solid ${colors.sageGreen}`,
                              borderRadius: "12px",
                              fontSize: "0.95rem",
                              fontWeight: "600",
                              color: colors.charcoal,
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            title={`Click to view ${stitch.name} in Stitch Dictionary`}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform =
                                "translateY(-2px)";
                              e.currentTarget.style.boxShadow =
                                "0 4px 8px rgba(0,0,0,0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            <div
                              style={{
                                color: colors.sageGreen,
                                fontWeight: "700",
                                marginBottom: "0.25rem",
                              }}
                            >
                              {stitch.abbreviation} →
                            </div>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                color: colors.brownGray,
                              }}
                            >
                              {stitch.name}
                            </div>
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

              {/* Yarn Colors — palette swatches */}
              {paletteColors.length > 0 && (
                <div
                  style={{
                    background: "#fff",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "12px",
                    padding: "1.5rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      marginBottom: "1rem",
                      color: colors.charcoal,
                      fontWeight: "700",
                    }}
                  >
                    Yarn Colors
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {paletteColors.map((yarn) => {
                      const isMobile = window.innerWidth < 768;
                      return (
                      <div
                        key={yarn.id}
                        style={{
                          display: "flex",
                          alignItems: isMobile ? "flex-start" : "center",
                          gap: "0.875rem",
                          padding: "0.75rem",
                          background: colors.bgCream,
                          borderRadius: "10px",
                          border: `2px solid ${colors.borderGreige}`,
                        }}
                      >
                        <div style={{ width: "48px", height: "48px", borderRadius: "8px", overflow: "hidden", flexShrink: 0 }}>
                          {yarn.image_url
                            ? <img src={yarn.image_url} alt={yarn.colorway_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <div style={{ width: "100%", height: "100%", background: yarn.hex || "#D6CFC7" }} />
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: "700", color: colors.charcoal, fontSize: "0.9rem" }}>
                            {yarn.brand_name}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: colors.darkGray }}>
                            {yarn.colorway_name}
                            {(yarn.total_yards || (yarn.yards_per_skein && yarn.skeins)) ? (
                              <span style={{ color: colors.brownGray }}>
                                {" · "}
                                {(() => {
                                  const yds = yarn.total_yards || Math.round(yarn.yards_per_skein * yarn.skeins);
                                  return isMobile
                                    ? `${yds} yds`
                                    : yarn.total_yards
                                      ? `${yds} yds`
                                      : `${yarn.yards_per_skein} yds/skein × ${yarn.skeins} skeins = ${yds} yds`;
                                })()}
                              </span>
                            ) : null}
                          </div>
                          {yarn.role && <div style={{ fontSize: "0.75rem", color: colors.brownGray }}>{yarn.role}</div>}
                          {isMobile && yarn.brand_url && (
                            <a href={yarn.brand_url} target="_blank" rel="noopener noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", marginTop: "0.4rem", padding: "0.3rem 0.75rem", background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`, color: "#fff", borderRadius: "50px", textDecoration: "none", fontSize: "0.75rem", fontWeight: "600" }}
                            >
                              Shop <ExternalLink size={11} />
                            </a>
                          )}
                        </div>
                        {!isMobile && yarn.brand_url && (
                          <a href={yarn.brand_url} target="_blank" rel="noopener noreferrer"
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", padding: "0.4rem 0.875rem", background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`, color: "#fff", borderRadius: "50px", textDecoration: "none", fontSize: "0.8rem", fontWeight: "600", flexShrink: 0 }}
                          >
                            Shop <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                      );
                    })}
                  </div>

                  {/* Affiliate Disclaimer */}
                  {paletteColors.some(y => y.brand_url) && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem",
                        background: "#fffbf0",
                        border: `1px solid ${colors.borderGreige}`,
                        borderRadius: "8px",
                        fontSize: "0.75rem",
                        color: colors.darkGray,
                        fontStyle: "italic",
                      }}
                    >
                      ℹ️ <strong>Affiliate Disclosure:</strong> Some links in this section may be affiliate links. If you purchase through these links, The Single Loop may earn a small commission at no additional cost to you.
                    </div>
                  )}
                </div>
              )}

              {/* Video Embed */}
              {selectedPattern.videoUrl &&
                getYouTubeId(selectedPattern.videoUrl) && (
                  <div style={{ marginBottom: "2rem", maxWidth: "800px", margin: "0 auto 2rem" }}>
                    <h3
                      style={{
                        fontSize: "1.3rem",
                        color: colors.charcoal,
                        marginBottom: "1rem",
                        fontWeight: "700",
                      }}
                    >
                      Video Tutorial
                    </h3>
                    <div
                      style={{
                        position: "relative",
                        paddingTop: "56.25%",
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: "#000",
                      }}
                    >
                      <iframe
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                        src={`https://www.youtube.com/embed/${getYouTubeId(
                          selectedPattern.videoUrl
                        )}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Pattern Tutorial"
                      />
                    </div>
                    {selectedPattern.videoCreator && (
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: colors.darkGray,
                          marginTop: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <Play size={14} />
                        Video by {selectedPattern.videoCreator} •
                        <a
                          href={selectedPattern.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: colors.dustyRust,
                            textDecoration: "none",
                            fontWeight: "600",
                          }}
                        >
                          Watch on YouTube ↗
                        </a>
                      </p>
                    )}
                  </div>
                )}
           
              {/* Action Buttons */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: "1rem",
                    paddingTop: "2rem",
                    borderTop: `2px solid ${colors.borderGreige}`,
                  }}
                >
                  {/* Primary CTA - View Free Pattern + Save icon */}
                  <div style={{ display: "flex", gap: "0.625rem", alignItems: "stretch" }}>
                    <a
                      href={selectedPattern.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        padding: "1rem",
                        background: `linear-gradient(135deg, ${colors.dustyRust} 0%, #a8674f 100%)`,
                        color: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        fontWeight: "700",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        boxShadow: `0 4px 15px rgba(193, 121, 91, 0.4)`,
                        transition: "all 0.3s",
                        textDecoration: "none",
                      }}
                    >
                      View Free Pattern
                      <ExternalLink size={18} />
                    </a>
                    {(() => {
                      const isSaved = savedPatterns.some(sp => sp.pattern_id === selectedPattern.id && sp.status === 'saved');
                      return (
                        <button
                          onClick={() => onSave(selectedPattern)}
                          title={isSaved ? "Saved" : "Save pattern"}
                          style={{
                            padding: "0 0.875rem",
                            background: "rgba(255,255,255,0.95)",
                            border: `2px solid ${colors.borderGreige}`,
                            borderRadius: "12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: isSaved ? colors.dustyRust : colors.brownGray,
                            transition: "all 0.2s",
                            flexShrink: 0,
                          }}
                        >
                          <Heart size={20} fill={isSaved ? colors.dustyRust : "none"} />
                        </button>
                      );
                    })()}
                  </div>

                  {/* Start Project */}
                  <button
                    onClick={() => onTrackProject(selectedPattern)}
                    style={{
                      padding: "1rem",
                      background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                      color: "#fff",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      boxShadow: `0 4px 15px rgba(163, 177, 138, 0.3)`,
                      transition: "all 0.3s",
                    }}
                  >
                    Start Project
                  </button>

                  {/* Customize */}
                  <button
                    onClick={() => onCustomize(selectedPattern)}
                    style={{
                      padding: "1rem",
                      background: `linear-gradient(135deg, ${colors.mustard} 0%, #c89b68 100%)`,
                      color: "#fff",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      boxShadow: `0 4px 15px rgba(212, 165, 116, 0.3)`,
                      transition: "all 0.3s",
                    }}
                  >
                    Design It
                  </button>
                </div>

                {/* Start Project Modal */}
                {showStartProjectModal && startingProjectPattern && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0,0,0,0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10001,
                      padding: '2rem'
                    }}
                    onClick={onCancelStart}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: '#fff',
                        borderRadius: '20px',
                        maxWidth: '500px',
                        width: '100%',
                        padding: '2rem',
                        fontFamily: fonts.body
                      }}
                    >
                      <h3 style={{
                        fontSize: '1.5rem',
                        fontFamily: fonts.display,
                        color: colors.charcoal,
                        marginBottom: '1rem'
                      }}>
                        Start This Project
                      </h3>
                
                      <p style={{
                        color: colors.darkGray,
                        marginBottom: '1.5rem'
                      }}>
                        Add any notes about yarn, colors, or modifications you're planning:
                      </p>
                
                      <textarea
                        value={startProjectNotes}
                        onChange={(e) => setStartProjectNotes(e.target.value)}
                        placeholder="e.g., Using mustard and cream yarn, making it bigger by adding 20 rows..."
                        style={{
                          width: '100%',
                          minHeight: '120px',
                          padding: '1rem',
                          border: `2px solid ${colors.borderGreige}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontFamily: fonts.body,
                          resize: 'vertical',
                          marginBottom: '1.5rem',
                          boxSizing: 'border-box'
                        }}
                      />
                
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                          onClick={onCancelStart}
                          style={{
                            flex: 1,
                            padding: '0.875rem',
                            background: colors.borderGreige,
                            color: colors.charcoal,
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={onConfirmStart}
                          style={{
                            flex: 1,
                            padding: '0.875rem',
                            background: colors.dustyRust,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Start Project!
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
            )}
          </div>
        </div>
  );
}

export default PatternDetailModal;
